import React from 'react'
import { DatePicker } from 'antd'
import moment from 'moment'
import { getFilterValue, getFilterTypeLabel, getAlertTypeColor, formatAlertTypeLabel, formatStatusLabel, STATUS_COLUMNS } from './utils'

const { RangePicker } = DatePicker

function FilterChips({
  data,
  activeFilterTypes,
  enabledFilterValues,
  searchTerm,
  onToggleFilterValue,
  onSelectAll,
  onSelectNone,
  getDynamicCount,
  alertDateRange,
  onAlertDateRangeChange,
  isDark = false
}) {
  const muted = isDark ? '#a1a1a1' : 'rgba(0,0,0,0.45)'
  const strong = isDark ? '#E0E0E0' : '#172b4d'
  const surface = isDark ? 'rgba(67, 79, 90, 0.86)' : 'rgba(255, 255, 255, 0.5)'
  const border = isDark ? '#495762' : '#dfe1e6'
  const chipInactiveBg = isDark ? '#3b4550' : '#e9ebf0'
  const chipInactiveText = isDark ? '#c9d2dc' : '#42526e'
  const chipActiveBg = isDark ? '#4a5661' : 'rgba(255, 255, 255, 0.9)'
  const chipActiveText = isDark ? '#E0E0E0' : '#172b4d'
  const hoverBg = isDark ? '#4a5661' : '#f4f5f7'
  const hoverText = isDark ? '#E0E0E0' : '#172b4d'

  if (activeFilterTypes.length === 0) {
    return null
  }

  const filterSections = []

  activeFilterTypes.forEach((filterType) => {
    if (filterType === 'alertDate') {
      filterSections.push(
        <div key="alertDate" style={{ marginBottom: 8 }}>
          <div style={{
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: muted,
            marginBottom: 4,
            fontWeight: 600
          }}>
            {getFilterTypeLabel('alertDate')}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <RangePicker
              value={alertDateRange && alertDateRange[0] && alertDateRange[1] ? [moment(alertDateRange[0]), moment(alertDateRange[1])] : undefined}
              onChange={(dates) => {
                if (!dates || dates.length < 2) {
                  onAlertDateRangeChange([null, null])
                  return
                }
                const start = dates[0] ? dates[0].startOf('day') : null
                const end = dates[1] ? dates[1].endOf('day') : null
                onAlertDateRangeChange([start, end])
              }}
              format="DD-MM-YYYY"
              placeholder={['From', 'To']}
              allowClear
              style={{ width: 220 }}
            />
          </div>
        </div>
      )
      return
    }

    const allValues = new Set()
    data.forEach((c) => {
      if (c.status === 'Action Taken') return
      const value = getFilterValue(c, filterType)
      if (value) {
        allValues.add(value)
      }
    })

    const sortedValues = filterType === 'status'
      ? STATUS_COLUMNS.map((col) => col.id).filter((id) => allValues.has(id))
      : Array.from(allValues).sort((a, b) => a.localeCompare(b))

    if (!enabledFilterValues[filterType] || !Array.isArray(enabledFilterValues[filterType])) {
      return
    }

    const enabledValues = new Set(enabledFilterValues[filterType])
    const chips = []
    let hasVisibleChips = false

    sortedValues.forEach((value) => {
      const dynamicCount = getDynamicCount(filterType, value)

      if (dynamicCount === 0) {
        return
      }

      hasVisibleChips = true
      const isActive = enabledValues.has(value)

      let chipBgColor = isActive ? chipActiveBg : chipInactiveBg
      let chipTextColor = isActive ? chipActiveText : chipInactiveText
      let chipBorderColor = isActive ? 'rgba(9, 30, 66, 0.25)' : 'rgba(9, 30, 66, 0.12)'
      let chipShadow = isActive
        ? '0 3px 8px rgba(9, 30, 66, 0.24), inset 0 0 0 1px rgba(255,255,255,0.22)'
        : '0 1px 3px rgba(9, 30, 66, 0.16), inset 0 0 0 1px rgba(255,255,255,0.55)'

      if (filterType === 'alertType') {
        const typeColor = getAlertTypeColor(value)
        if (isActive) {
          chipBgColor = typeColor.bg
          chipTextColor = typeColor.color
          chipBorderColor = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(9, 30, 66, 0.12)'
          chipShadow = isDark
            ? '0 4px 10px rgba(0,0,0,0.34), inset 0 0 0 1px rgba(255,255,255,0.08)'
            : '0 3px 8px rgba(9, 30, 66, 0.2), inset 0 0 0 1px rgba(255,255,255,0.42)'
        }
      }

      chips.push(
        <button
          key={value}
          onClick={() => onToggleFilterValue(filterType, value)}
          style={{
            fontSize: 12,
            padding: '6px 10px',
            borderRadius: 999,
            border: `1px solid ${chipBorderColor}`,
            background: chipBgColor,
            color: chipTextColor,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            transition: 'all 0.15s ease',
            boxShadow: chipShadow,
            fontWeight: filterType === 'alertType' ? 600 : 'normal'
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget
            if (!isActive) {
              el.style.transform = 'translateY(-1px)'
              el.style.boxShadow = isDark
                ? '0 3px 7px rgba(0, 0, 0, 0.35), inset 0 0 0 1px rgba(255,255,255,0.08)'
                : '0 2px 6px rgba(9, 30, 66, 0.22), inset 0 0 0 1px rgba(255,255,255,0.45)'
            }
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget
            if (!isActive) {
              el.style.transform = 'translateY(0)'
              el.style.boxShadow = chipShadow
            } else if (filterType === 'alertType') {
              const typeColor = getAlertTypeColor(value)
              el.style.background = typeColor.bg
              el.style.color = typeColor.color
            }
          }}
        >
          <span style={{ background: 'transparent', padding: 0, boxShadow: 'none', border: 'none' }}>{filterType === 'alertType' ? (formatAlertTypeLabel(value) || value) : filterType === 'status' ? (formatStatusLabel(value) || value) : value}</span>
          <span style={{
            fontWeight: 700,
            fontSize: 11,
            color: chipTextColor,
            background: isActive
              ? (isDark ? 'rgba(0, 0, 0, 0.22)' : 'rgba(9, 30, 66, 0.12)')
              : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(9, 30, 66, 0.08)'),
            borderRadius: 999,
            minWidth: 18,
            height: 18,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 6px',
            lineHeight: 1
          }}>
            {dynamicCount}
          </span>
        </button>
      )
    })

    if (hasVisibleChips) {
      filterSections.push(
        <div key={filterType} style={{ marginBottom: 8 }}>
          <div style={{
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: muted,
            marginBottom: 4,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <span>{getFilterTypeLabel(filterType)}</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={() => onSelectAll(filterType)}
                style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  borderRadius: 4,
                  border: 'none',
                  background: 'transparent',
                  color: muted,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget
                  el.style.background = hoverBg
                  el.style.color = hoverText
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget
                  el.style.background = 'transparent'
                  el.style.color = muted
                }}
              >
                Select All
              </button>
              <button
                onClick={() => onSelectNone(filterType)}
                style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  borderRadius: 4,
                  border: 'none',
                  background: 'transparent',
                  color: muted,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget
                  el.style.background = hoverBg
                  el.style.color = hoverText
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget
                  el.style.background = 'transparent'
                  el.style.color = muted
                }}
              >
                Select None
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, width: '100%', maxWidth: '100%' }}>
            {chips}
          </div>
        </div>
      )
    }
  })

  if (filterSections.length === 0) {
    return null
  }

  return (
    <div style={{
      padding: '8px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      borderBottom: `1px solid ${border}`,
      background: surface,
      width: '100%',
      maxWidth: '100%'
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: '100%',
        maxWidth: '1200px'
      }}>
        {filterSections}
      </div>
    </div>
  )
}

export default React.memo(FilterChips)
